/**
 * Extended types for the HI-SI Creative Portal
 * These types extend the base execution types with portal-specific data
 * Now with HIGH SIGNAL visual enhancements
 */

import { ExecutionStatus, StageType } from './execution';

export interface UserFeedbackEvaluation {
  type: 'user_feedback';
  decision: 'improve' | 'rewrite' | 'accept_with_notes';
  feedback: string;
  priority: 'override' | 'supplement';
  scores_override?: Partial<QualityScores>;
  preserve_elements?: string[];
  specific_issues?: string[];
  suggestions?: string[];
  timestamp: string;
  stage: StageType;
  iteration_number: number;
}

export interface QualityScores {
  hook_effectiveness: number;
  narrative_clarity: number;
  visual_coherence: number;
  persona_consistency: number;
  anti_cliche_score: number;
  overall: number;
}

export interface IterationSummary {
  iteration_number: number;
  what_worked: string[];
  what_failed: string[];
  timestamp: string;
}

export interface StageData {
  status: 'pending' | 'running' | 'completed' | 'failed';
  iterations: number;
  quality: number;
  persona?: string;
  lastUpdate?: string;
  output?: any;
  audioUrl?: string; // TTS audio URL for script stage
}

export interface ExecutionPortalData extends ExecutionStatus {
  stages: Record<StageType, StageData>;
}

export interface CascadePlan {
  trigger_stage: StageType;
  affected_stages: StageType[];
  preserved_refinements: string[];
  lost_refinements: string[];
  execution_strategy: string;
  estimated_duration: number;
  total_cost: number;
  confidence_score: number;
}

// Visual Enhancement Types - HIGH SIGNAL
export interface NodeEnhancements {
  size: {
    base: string;
    hover: string;
    active: string;
  };
  
  glow: {
    highQuality: {
      shadow: string;
      border: string;
      pulseAnimation: string;
    };
    mediumQuality: {
      shadow: string;
      border: string;
    };
    lowQuality: {
      shadow: string;
      border: string;
    };
  };
  
  backdrop: string;
}

export interface ConnectionEnhancements {
  idle: {
    stroke: string;
    strokeWidth: number;
    filter: string;
  };
  
  active: {
    stroke: string;
    strokeWidth: number;
    animation: string;
    glow: string;
  };
  
  completed: {
    stroke: string;
    strokeWidth: number;
    glow: string;
  };
}

export interface NodeInformation {
  primary: {
    icon: string;
    name: string;
    quality: string;
  };
  
  secondary: {
    persona: string;
    iterations: string;
    duration: string;
    status: string;
  };
  
  excluded: string[];
}

// Utility to get CSS custom property values at runtime
const getCSSVar = (varName: string) => {
  if (typeof window === 'undefined') return 'rgb(0, 0, 0)'; // SSR fallback
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || 'rgb(0, 0, 0)';
};

export const colorSystem = {
  // Quality spectrum - uses semantic tokens
  get excellence() { return getCSSVar('--success'); },
  get acceptable() { return getCSSVar('--warning'); },
  get insufficient() { return getCSSVar('--danger'); },
  
  // State indicators
  get processing() { return getCSSVar('--warning'); },
  get waiting() { return getCSSVar('--fg-muted'); },
  get error() { return getCSSVar('--danger'); },
  
  // Interaction - uses semantic overlay tokens
  get hover() { return getCSSVar('--bg-interactive-hover'); },
  get active() { return getCSSVar('--bg-interactive-active'); },
  get disabled() { return getCSSVar('--bg-interactive-idle'); }
};